// src/services/blogPostService.js
import api from '../utils/api'; // Your API instance

const BLOG_POST_BASE_URL = '/blogs';

const blogPostService = {
  // Get all blog posts with pagination, filtering, and search
  getBlogPosts: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add search
      if (params.search) queryParams.append('search', params.search);
      
      // Add filters
      if (params.status) queryParams.append('status', params.status);
      if (params.categoryId) queryParams.append('categoryId', params.categoryId);
      if (params.authorId) queryParams.append('authorId', params.authorId);
      if (params.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      
      // Add sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await api.get(`${BLOG_POST_BASE_URL}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get single blog post by ID
  getBlogPostById: async (id) => {
    try {
      const response = await api.get(`${BLOG_POST_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get blog post by slug
  getBlogPostBySlug: async (slug) => {
    try {
      const response = await api.get(`${BLOG_POST_BASE_URL}/slug/${slug}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new blog post
  createBlogPost: async (postData) => {
    try {
      const response = await api.post(BLOG_POST_BASE_URL, postData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update blog post
  updateBlogPost: async (id, postData) => {
    try {
      const response = await api.put(`${BLOG_POST_BASE_URL}/${id}`, postData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Partial update blog post
  patchBlogPost: async (id, postData) => {
    try {
      const response = await api.patch(`${BLOG_POST_BASE_URL}/${id}`, postData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete blog post
  deleteBlogPost: async (id) => {
    try {
      const response = await api.delete(`${BLOG_POST_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update blog post status
  updateBlogPostStatus: async (id, status) => {
    try {
      const response = await api.patch(`${BLOG_POST_BASE_URL}/${id}/status`, { status });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get blog comments
  getBlogComments: async (postId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);
      
      const response = await api.get(
        `${BLOG_POST_BASE_URL}/${postId}/comments?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update comment status
  updateCommentStatus: async (postId, commentId, status) => {
    try {
      const response = await api.patch(
        `${BLOG_POST_BASE_URL}/${postId}/comments/${commentId}/status`,
        { status }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete comment
  deleteComment: async (postId, commentId) => {
    try {
      const response = await api.delete(`${BLOG_POST_BASE_URL}/${postId}/comments/${commentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteDocument: async (postId, documentId) => {
    try {
      const response = await api.delete(`${BLOG_POST_BASE_URL}/${postId}/documents/${documentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get blog categories
  getBlogCategories: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      
      const response = await api.get(`/categories?type=BLOG&${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get blog tags
  getBlogTags: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      
      const response = await api.get(`/tags?type=BLOG&${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload featured image
  uploadFeaturedImage: async (postId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        `${BLOG_POST_BASE_URL}/${postId}/featured-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload gallery images
  uploadGalleryImages: async (postId, files) => {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await api.post(
        `${BLOG_POST_BASE_URL}/${postId}/gallery`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete gallery image
  deleteGalleryImage: async (postId, imageId) => {
    try {
      const response = await api.delete(`${BLOG_POST_BASE_URL}/${postId}/gallery/${imageId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get related posts
  getRelatedPosts: async (postId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      
      const response = await api.get(
        `${BLOG_POST_BASE_URL}/${postId}/related?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Bulk delete blog posts
  bulkDeleteBlogPosts: async (ids) => {
    try {
      const response = await api.delete(`${BLOG_POST_BASE_URL}/bulk`, { data: { ids } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get blog statistics
  getBlogStats: async () => {
    try {
      const response = await api.get(`${BLOG_POST_BASE_URL}/stats`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default blogPostService;